import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { FrequencyTabContent, HotColdTabContent, TrendsTabContent, PairsTabContent } from '../components/analysis/AnalysisTabs';
import { toast } from 'sonner';
import { Flame, TrendingUp, Calendar, Hash } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Analysis = () => {
  const { lotteryId } = useParams();
  const { token } = useAuth();
  const [lotteries, setLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('365');

  const fetchLotteries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lotteries`);
      setLotteries(response.data);
      
      if (lotteryId) {
        const lottery = response.data.find(l => l.id === lotteryId);
        setSelectedLottery(lottery || response.data[0]);
      } else if (response.data.length > 0) {
        setSelectedLottery(response.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar loterías');
    } finally {
      setLoading(false);
    }
  }, [lotteryId]);

  const fetchAnalysis = useCallback(async () => {
    if (!selectedLottery) return;
    try {
      const response = await axios.get(`${API}/analysis/${selectedLottery.id}?days=${period}`);
      setAnalysis(response.data);
    } catch (error) {
      console.warn('Error fetching analysis:', error.message);
    }
  }, [selectedLottery, period]);

  const fetchResults = useCallback(async () => {
    if (!selectedLottery) return;
    try {
      const response = await axios.get(`${API}/results?lottery_id=${selectedLottery.id}&limit=100`);
      setResults(response.data);
    } catch (error) {
      console.warn('Error fetching results:', error.message);
    }
  }, [selectedLottery]);

  useEffect(() => {
    fetchLotteries();
  }, [fetchLotteries]);

  useEffect(() => {
    if (selectedLottery) {
      fetchAnalysis();
      fetchResults();
    }
  }, [selectedLottery, period, fetchAnalysis, fetchResults]);

  const frequencyData = useMemo(() => {
    if (!analysis?.frequency) return [];
    return Object.entries(analysis.frequency)
      .map(([num, count]) => ({ number: parseInt(num), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [analysis]);

  const trendData = useMemo(() => {
    if (!results.length) return [];
    return results.slice(0, 30).reverse().map((r) => ({
      date: r.draw_date.slice(5),
      avg: r.numbers.reduce((a, b) => a + b, 0) / r.numbers.length
    }));
  }, [results]);

  const distributionData = useMemo(() => {
    if (!analysis?.frequency) return [];
    const ranges = [
      { name: '0-19', min: 0, max: 19 },
      { name: '20-39', min: 20, max: 39 },
      { name: '40-59', min: 40, max: 59 },
      { name: '60-79', min: 60, max: 79 },
      { name: '80-99', min: 80, max: 99 },
    ];
    return ranges.map(range => {
      let count = 0;
      Object.entries(analysis.frequency).forEach(([num, freq]) => {
        const n = parseInt(num);
        if (n >= range.min && n <= range.max) count += freq;
      });
      return { name: range.name, value: count };
    });
  }, [analysis]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="analysis-title">
              Análisis Estadístico
            </h1>
            <p className="text-slate-400">Explora patrones y tendencias</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Select
              value={selectedLottery?.id}
              onValueChange={(value) => {
                const lottery = lotteries.find(l => l.id === value);
                setSelectedLottery(lottery);
              }}
            >
              <SelectTrigger className="w-[200px] bg-slate-900 border-slate-700 text-white" data-testid="analysis-lottery-selector">
                <SelectValue placeholder="Seleccionar lotería" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {lotteries.map((lottery) => (
                  <SelectItem 
                    key={lottery.id} 
                    value={lottery.id}
                    className="text-white hover:bg-slate-800 focus:bg-slate-800"
                  >
                    {lottery.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white" data-testid="period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="30" className="text-white hover:bg-slate-800">30 días</SelectItem>
                <SelectItem value="90" className="text-white hover:bg-slate-800">90 días</SelectItem>
                <SelectItem value="180" className="text-white hover:bg-slate-800">6 meses</SelectItem>
                <SelectItem value="365" className="text-white hover:bg-slate-800">1 año</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-6">
          <DisclaimerBanner variant="inline" />
        </div>

        <Tabs defaultValue="frequency" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="frequency" className="data-[state=active]:bg-green-600" data-testid="tab-frequency">
              <Hash size={16} className="mr-2" />
              Frecuencia
            </TabsTrigger>
            <TabsTrigger value="hotcold" className="data-[state=active]:bg-green-600" data-testid="tab-hotcold">
              <Flame size={16} className="mr-2" />
              Calientes/Fríos
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-green-600" data-testid="tab-trends">
              <TrendingUp size={16} className="mr-2" />
              Tendencias
            </TabsTrigger>
            <TabsTrigger value="pairs" className="data-[state=active]:bg-green-600" data-testid="tab-pairs">
              <Calendar size={16} className="mr-2" />
              Pares/Tripletas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="frequency" className="space-y-6">
            <FrequencyTabContent frequencyData={frequencyData} distributionData={distributionData} />
          </TabsContent>

          <TabsContent value="hotcold" className="space-y-6">
            <HotColdTabContent analysis={analysis} />
          </TabsContent>

          <TabsContent value="trends" className="space-y-6">
            <TrendsTabContent trendData={trendData} analysis={analysis} />
          </TabsContent>

          <TabsContent value="pairs" className="space-y-6">
            <PairsTabContent analysis={analysis} />
          </TabsContent>
        </Tabs>
      </main>

      <DisclaimerBanner variant="fixed" />
    </div>
  );
};

export default Analysis;
